import {BasketResponseData} from '@aboutyou/backbone/endpoints/basket/getBasket';
import {
  GRPC_HEADER_PROPERTY_NAME,
  GrpcResponseError,
  getRawGrpcResponseErrorStatus,
  useGrpcRequestMutation,
} from '@aboutyou/data-fetcher';

import {Add2BasketEvent, Log} from '@aboutyou/mlm';
// eslint-disable-next-line monorepo/no-internal-import
import {useTrackBasketAction} from '@aboutyou/tracking/src/trackingHooks/trackBasketAction';
// eslint-disable-next-line monorepo/no-internal-import
import {convertProductToTrackingBasketItem} from '@aboutyou/tracking/src/utils/getTrackingBasketItem';
// eslint-disable-next-line monorepo/no-internal-import
import type {ProductTrackingData} from '@aboutyou/tracking/src/trackerComponents/TrackPromotions';
import {startSpan} from '@aboutyou/utils';
import {useCallback} from 'react';
import {useFullSession} from '../userStore/useSession';
import {useBasketSession} from './basket.selectors';
import {AddToBasketError} from './errors';
// eslint-disable-next-line monorepo/no-internal-import
import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {BasketService_AddItemLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/service.grpc.lazy';
// eslint-disable-next-line monorepo/no-internal-import
import {useReductionsStateRequestParameterCallback} from '@aboutyou/logic-components/src/features/ReductionsState/useReductionsStateRequestParameter';
import {useBasketToken} from './BasketTokenProvider';

export type BasketState = BasketResponseData;

export interface AddToBasketTrackingData {
  linkedContentId?: string;
  linkedContentType?: string;
  sponsoredType?: string | null;
  sortingChannel?: string | null;
}

export type BasketMutationResponse =
  | BasketMutationSuccess
  | BasketMutationError;

export type BasketMutationError = {
  failed: true;
  message?: string;
  code?: number;
  captchaFailed?: boolean;
};

export type BasketMutationSuccess = {
  failed: false;
  freeDeliverySection?: FreeDeliverySection;
  statusSuccessSections: AddItemResponseStatus_Success_Section[];
};

export const BasketResponseStatus = {
  OK: 200,
  SUCCESSFUL_CREATED: [200, 201],
  OUTOFSTOCK: [404, 412],
  CAPTCHA_REQUIRED: 418,
  BLOCKED: [403, 418, 429],
  MAX_QUANTITY: 413,
  MAX_ORDER_QUANTITY_REACHED: 206,
};

const getSizeSourceValue = (
  sizeSource: 'shop' | 'vendor' = 'shop',
):
  | {
      type: {
        $case: 'shop';
        shop: {};
      };
    }
  | {
      type: {
        $case: 'vendor';
        vendor: {};
      };
    } => {
  switch (sizeSource) {
    case 'shop':
      return {
        type: {
          $case: 'shop',
          shop: {},
        },
      };
    case 'vendor':
      return {
        type: {
          $case: 'vendor',
          vendor: {},
        },
      };
  }
};

export const useAddToBasketMutation = ({
  trackingDataGA,
}: {
  trackingDataGA?: ProductTrackingData & {
    brandName: string;
    sizeFinderRecommendations?: string;
    badges: string[];
  };
} = {}) => {
  const {session, isLoading: isSessionLoading} = useBasketSession();
  const addToBasketMutate = useAddItemToBasketMutate({
    trackingDataGA,
  });

  return useCallback(
    async ({
      productId,
      sizeId,
      captchaCode,
      trackingDataBE,
      sizeSource,
      couponPromotionId,
      skipFreeDeliveryMovSection,
      sizeFinderRecommendations,
    }: {
      productId?: number;
      couponPromotionId?: string | null;
      sizeId?: number;
      captchaCode?: string;
      trackingDataBE: AddToBasketTrackingData | undefined;
      skipFreeDeliveryMovSection?: boolean;
      sizeSource?: 'shop' | 'vendor';
      sizeFinderRecommendations?: string;
    }): Promise<BasketMutationResponse> => {
      if (!session || !sizeId) {
        // session can be loading and we dont want to throw error if that happens
        // session is loading when user directly navigates to a product with one size and its incognito browser or similar environment
        // session can be initially null
        if (isSessionLoading) {
          logAddToBasket({
            code: -1,
            statusText: 'Session is loading',
            success: false,
            productId,
            sizeId,
            endAdd2BasketSpan: () => 0,
            sId: '',
          });
          return {failed: true, code: -1};
        }

        logAddToBasket({
          code: -1,
          statusText: 'No session found',
          success: false,
          productId,
          sizeId,
          endAdd2BasketSpan: () => 0,
          sId: '',
        });

        throw new Error(
          'Missing necessary properties. Session and sizeId not available so add to basket failed',
        );
      }

      return addToBasketMutate({
        productId: productId!,
        couponPromotionId: couponPromotionId ?? undefined,
        sizeId,
        captchaCode,
        trackingData: trackingDataBE,
        sizeSource,
        skipFreeDeliveryMovSection,
        sizeFinderRecommendations,
      });
    },
    [session, addToBasketMutate, isSessionLoading],
  );
};

const logAddToBasket = ({
  code,
  statusText,
  success,
  productId,
  sizeId,
  endAdd2BasketSpan,
  sId,
}: {
  code: number;
  statusText?: string;
  success: boolean;
  productId: number | undefined;
  sizeId: number | undefined;
  sId: string;
  endAdd2BasketSpan: () => number;
}) => {
  Log.compare({
    actionName: Add2BasketEvent,
    success,
    attributes: {
      code,
      productId,
      variantId: sizeId!,
      duration: endAdd2BasketSpan(),
      statusText,
      sId,
    },
  });
};

const useAddItemToBasketMutate = ({
  trackingDataGA,
}: {
  trackingDataGA?: ProductTrackingData & {
    brandName: string;
    badges: string[];
  };
} = {}) => {
  const getReductionsState = useReductionsStateRequestParameterCallback();
  const session = useFullSession();
  const basketSession = useBasketSession();
  const trackAddToBasket = useTrackBasketAction();
  const {token: minimalStateToken} = useBasketToken();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
  const sId = basketSession.session?.resolvedId!;

  const queryFunction = useGrpcRequestMutation(BasketService_AddItemLazy);

  const addItemToBasket = async ({
    productId,
    sizeId,
    captchaCode,
    sizeSource,
    trackingData,
    couponPromotionId,
    skipFreeDeliveryMovSection,
    sizeFinderRecommendations,
  }: {
    productId: number;
    couponPromotionId?: string;
    sizeId: number;
    captchaCode?: string;
    trackingData?: AddToBasketTrackingData;
    sizeSource?: 'shop' | 'vendor';
    skipFreeDeliveryMovSection?: boolean;
    sizeFinderRecommendations?: string;
  }): Promise<BasketMutationResponse> => {
    const endAdd2BasketSpan = startSpan();

    const request = queryFunction.mutateAsync({
      productId,
      sizeId,
      couponPromotionId: couponPromotionId ?? null,
      reductionsState: await getReductionsState(),
      session: session!,
      trackingData: {
        originDevice: process.env.STACK!,
        linkedContentId: trackingData?.linkedContentId
          ? String(trackingData.linkedContentId)
          : undefined,
        linkedContentType: trackingData?.linkedContentType || undefined,
        sortingChannel: trackingData?.sortingChannel || undefined,
        sponsoredType: trackingData?.sponsoredType || undefined,
        sizeFinderRecommendations,
        itemBadge: trackingDataGA?.badges.join(',') || undefined,
      },
      basketToken: minimalStateToken,
      sizeSource: getSizeSourceValue(sizeSource),
      skipFreeDeliveryMovSection: Boolean(skipFreeDeliveryMovSection),
      [GRPC_HEADER_PROPERTY_NAME]: {
        'x-variant-id': sizeId.toString(),
        ...(captchaCode ? {'x-captcha-code': captchaCode} : {}),
      },
    });

    return request
      .then(async response => {
        if (response.status.status?.$case === 'success') {
          const statusSuccessSections = response.status.status.success.section;

          for (const item of response.status.status.success.section) {
            if (item.type?.$case === 'reducedQuantity') {
              logAddToBasket({
                code: -1,
                statusText: 'Reduced quantity',
                success: false,
                productId,
                sizeId,
                endAdd2BasketSpan,
                sId,
              });

              return {
                failed: true,
                captchaFailed: false,
                message: item.type.reducedQuantity.message,
                code: -1,
              } as BasketMutationError;
            }
          }

          const trackingItem = response.basketTracker.items.find(item => {
            return item.size.sizeId === sizeId;
          });

          if (trackingItem && trackingDataGA?.brandName) {
            trackingItem.brand.name = trackingDataGA.brandName;
          }

          // we need to wait for fetching of new basket to finish before adding trace of transaction
          // There is a problem where if we wait for response and miniADP closes before this is resolves we wont get updated basket from provider
          // to avoid that we had to refactor refetch call to return latest state.
          // it was already doing that but it was blocked by types
          if (trackingItem) {
            trackAddToBasket({
              type: 'add-to-basket',
              basketItem: convertProductToTrackingBasketItem({
                basketTracker: trackingItem,
              }),
              basketTracker: response.basketTracker,
              trackingData: trackingDataGA,
              badges: trackingDataGA?.badges || [],
            });
          }

          logAddToBasket({
            code: 200,
            success: true,
            productId,
            sizeId,
            endAdd2BasketSpan,
            sId,
          });

          return {
            failed: false,
            freeDeliverySection: response.freeDeliverySection,
            statusSuccessSections,
          } as BasketMutationSuccess;
        } else {
          switch (response.status.status?.error.type?.$case) {
            case 'captchaRequired': {
              const error = response.status.status?.error.type?.captchaRequired;
              logAddToBasket({
                code: BasketResponseStatus.CAPTCHA_REQUIRED,
                statusText: 'Captcha required',
                success: false,
                productId,
                sizeId,
                endAdd2BasketSpan,
                sId,
              });

              return {
                failed: true,
                captchaFailed: true,
                message: error.message,
                code: BasketResponseStatus.CAPTCHA_REQUIRED,
              } as BasketMutationError;
            }
            case 'error': {
              const error = response.status.status?.error.type?.error;
              logAddToBasket({
                code: error.responseStatusCode,
                statusText: error.message,
                success: false,
                productId,
                sizeId,
                endAdd2BasketSpan,
                sId,
              });

              return {
                failed: true,
                message: error.message,
                code: error.responseStatusCode,
              } as BasketMutationError;
            }
            default:
              logAddToBasket({
                code: -1,
                statusText: 'unknown error type happened in Add2Basket',
                success: false,
                productId,
                sizeId,
                endAdd2BasketSpan,
                sId,
              });
              return {failed: true, code: -1} as BasketMutationError;
          }
        }
      })
      .catch(err => {
        const {message} = err as GrpcResponseError;
        const statusText =
          message || 'Unknown network error happened in Add2Basket';

        // raw response error code could include non-grpc network related error codes, like: 403, 429, 418 etc
        const errorCode = getRawGrpcResponseErrorStatus(err);

        const isBlockedByCloudflare = isBlockedByCloudflareWorker(errorCode);
        if (isBlockedByCloudflare) {
          return {
            failed: true,
            captchaFailed: true,
            message: 'Blocked by CF rate-limiting',
            code: BasketResponseStatus.CAPTCHA_REQUIRED,
          } as BasketMutationError;
        } else {
          logAddToBasket({
            code: errorCode || -1,
            statusText,
            success: false,
            productId,
            sizeId,
            endAdd2BasketSpan,
            sId,
          });
          // message is reset to empty string to avoid showing this message to the user, we have translation fallback error toast in place when message is empty
          const basketError = new AddToBasketError('');
          basketError.code = errorCode;

          throw basketError;
        }
      });
  };
  return addItemToBasket;
};

const isBlockedByCloudflareWorker = (errorCode: number): boolean => {
  return errorCode === BasketResponseStatus.CAPTCHA_REQUIRED;
};
